import { Map, List } from 'immutable';
import createReducer from './utils/createReducer';
import { curry, forEach, map, mapObjIndexed, omit } from 'ramda';
import { addNode, getEvalSceneGraph } from './sceneGraph';
import { fetchText, addFile, getActiveAssets } from './assets';
import { addClip } from './animation';
import { LOAD_SCENE, LOADING_FILE, LOADED_FILE, LOADING_FILE_ERROR, CLEAR_SCENE } from './actionTypes';

/**
 * Loading, reading, exporting of the scene.
 *
 * @module sceneIO
 * @public
 */

const USE_SCENE = 'USE_SCENE';
const LOADING_SCENEGRAPH = 'LOADING_SCENEGRAPH';
const LOADED_SCENEGRAPH = 'LOADED_SCENEGRAPH';
const ERROR_LOADING_SCENEGRAPH = 'ERROR_LOADING_SCENEGRAPH';
const RENDERING_SCENE = 'RENDERING_SCENE';
const RENDERED_SCENE = 'RENDERED_SCENE';
const PRELOADING_SCENE='PRELOADING_SCENE';
const PRELOADED_SCENE = 'PRELOADED_SCENE';
const LOADED_SCENE = 'LOADED_SCENE';
const SCENE_ERROR = 'SCENE_ERROR';
const PRELOAD_COMMAND = 'PRELOAD_COMMAND';

export const SCENE_PHASES = {
  FAILED: -1,
  UNKNOWN: 0,
  LOADING_SCENEGRAPH: 1,
  LOADED_SCENEGRAPH: 2,
  RENDERING: 3,
  PRELOADING: 4,
  PRELOADED: 5,
  LOADED: 6,
  RENDERED: 7,
};

const initialState = Map({
  id: null,       // Active scene id
  errors: List(), // List of scene errors
  phase: SCENE_PHASES.UNKNOWN,
  progress: 0,
  loadingFiles: Map(),
  loadedFiles: Map(),
  loaded: false,
  preloadCommands: List(),
});

function setProgress(state, createEvent) {
  if (state.get('phase') !== SCENE_PHASES.RENDERING) return state;

  const loadingFiles = state.get('loadingFiles').size;
  const loadedFiles = state.get('loadedFiles').size;
  const totalFiles = loadingFiles + loadedFiles;
  const progress = totalFiles === 0 ? 1 : (1 + loadedFiles / totalFiles) / 2;
  //console.log('progress: ', loadingFiles, loadedFiles, progress);

  if (progress < 1) {
    createEvent('loading', progress);
    return state.set('progress', progress);
  }

  createEvent('loading', 1);
  return state.set('progress', 1);
};

const reducer = createReducer(initialState, {
  [USE_SCENE](state, id) {
    return state.set('id', id);
  },

  [LOADING_SCENEGRAPH](state, id) {
    const hasActiveScene = state.get('loaded') || (state.get('id') && state.get('id') !== id);
    return hasActiveScene ? state : state.set('phase', SCENE_PHASES.LOADING_SCENEGRAPH).set('progress', 1 / 4);
  },

  [LOADED_SCENEGRAPH](state, id) {
    const hasActiveScene = state.get('loaded') || (state.get('id') && state.get('id') !== id);
    return hasActiveScene ? state : state.set('phase', SCENE_PHASES.LOADED_SCENEGRAPH).set('progress', 1 / 2);
  },

  [ERROR_LOADING_SCENEGRAPH](state, xhr, { action }) {
    const { error } = action;
    const msg = `Error Loading Scene: ${error.status} ${error.statusText}`; //' + (error && error.response ? ': '+error.response : '');
    return state.updateIn(['errors'], List(), list => list.push(msg));
  },

  [RENDERING_SCENE](state, payload, { createEvent }) {
    return setProgress(state.set('phase', SCENE_PHASES.RENDERING), createEvent);
  },

  [LOADING_FILE](state, id) {
    return state.setIn(['loadingFiles', id], true);
  },

  [LOADED_FILE](state, id, { createEvent }) {
    return setProgress(state.deleteIn(['loadingFiles', id]).setIn(['loadedFiles', id], true), createEvent);
  },

  [LOADING_FILE_ERROR](state, id, { createEvent }) {
    return setProgress(state.deleteIn(['loadingFiles', id]), createEvent);
  },

  [PRELOADING_SCENE](state, _, {createEvent}) {
    createEvent('preloading');
    return state.set('phase', SCENE_PHASES.PRELOADING);
  },

  [PRELOADED_SCENE](state, _, {createEvent}) {
    createEvent('preloaded');
    return state.set('phase', SCENE_PHASES.PRELOADED);
  },

  [PRELOAD_COMMAND](state, fn) {
    return state.updateIn(['preloadCommands'], List(), list => list.push(fn));
  },

  [LOADED_SCENE](state, _, { createEvent }) {
    createEvent('loaded');
    return state.set('loaded', true).set('phase', SCENE_PHASES.LOADED);
  },

  [RENDERED_SCENE](state, _, { createEvent }) {
    createEvent('rendered');
    return state.set('phase', SCENE_PHASES.RENDERED);
  },

  [SCENE_ERROR](state, msg) {
    return state.updateIn(['errors'], List(), list => list.push(msg));
  },

  [CLEAR_SCENE](state) {
    return initialState;
  },
});

const apiRoot = __URLROOT__ + '/api';

export function reportError(msg) {
  return { type: SCENE_ERROR, payload: msg };
}

/**
 * sets the active scene.
 *
 * @param {string} id the scene uuid
 */
export function useScene(id) {
  return { type: USE_SCENE, payload: id };
}


/**
 * Returns the active scene Id
 *
 * @public
 * @returns {uuid}
 *
 */
export function getSceneId(store) {
  return store.getIn(['sceneIO', 'id']);
};

/**
 * Loads the published scene
 *
 * @public
 * @param {string} id the scene uuid.
 * @param {string} transitString the published, exported scene
 * @returns {Promise}
 *
 */
export function loadScene(id, transitString, loadTime) {
  return (store) => {
    const payload = store.read(transitString);
    store.logPageView(`/player/v2/${id}`, loadTime);
    return store.dispatch({ type: LOAD_SCENE, payload });
  };
}

export function errorLoadingScene(id) {
  return reportError('Error Loading Scene');
}

export function markRenderingScene() {
  return { type: RENDERING_SCENE };
}

export function markRenderedScene() {
  return { type: RENDERED_SCENE };
}

const LOAD_SLOW_THRESHOLD = 10000;
const LOAD_FAIL_THRESHOLD = 30000;

/**
 * Fetch the scene
 *
 * If `hash` is provided it will fetch directly from resources, otherwise it will fetch from
 * `/api/scenes/:uuid/published`. By default, it will fetch the latest published version. If you
 *  wish to ensure you are fetching the latest scene version, set `waitForPublish: true`.
 *
 *  @public
 *  @param {uuid} sceneId
 *  @param {String} hash - md5 hash of published content (Optional)
 *  @param {Object} options
 *  @param {Boolean} options.waitForPublish Wait for the latest scene version to be published (default false);
 *
 */
export function fetch(sceneId, hash, options = {}) {
  const startTime = new Date();
  const wait = options.waitForPublish;

  return (store) => {
    if (store.getIn(['sceneGraph', sceneId])) return Promise.resolve(true);

    const fetch = hash ?
      fetchText(hash)(store) :
      store.callApi({
        contentType: 'text',
        types: [LOADING_SCENEGRAPH, LOADED_SCENEGRAPH, ERROR_LOADING_SCENEGRAPH],
        url: `${apiRoot}/scenes/${sceneId}/published?wait=${wait ? 'true' : 'false'}`,
        queryKey: 'SCENE' + sceneId,
        payload: sceneId,
      });


    let slowLoadTimeout = setTimeout(() => {
      if (__BROWSER__) {
        store.error(new Error('Slow Load'), null, { sceneId: sceneId });
      } else {
        console.log('Slow Load');
      }
    }, LOAD_SLOW_THRESHOLD);

    let loadFailureTimeout = setTimeout(() => {
      if (__BROWSER__) {
        store.error(new Error('Load Failure'), null, { sceneId: sceneId });
      } else {
        console.log('Really Slow Load');
      }
    }, LOAD_FAIL_THRESHOLD);


    const promise = fetch.then((transit) => {
      clearTimeout(slowLoadTimeout);
      clearTimeout(loadFailureTimeout);
      return store.dispatch(loadScene(sceneId, transit, new Date() - startTime));
    });

    return promise;
  };
};

export function savePublished() {
  return (store) => {
    const transit = writeScene(store);
    const sceneId = getSceneId(store);

    return store.callApi({
      contentType: 'text',
      url: `${apiRoot}/scenes/${sceneId}/published`,
      method: 'PUT',
      body: JSON.stringify({ content: transit }),
    });
  };
}

/**
 * Fetch, Load and Use published scene
 *
 * If `hash` is provided it will fetch directly from resources, otherwise it will fetch from
 * `/api/scenes/:uuid/published`.
 *
 *  @public
 *  @param {uuid} sceneId
 *  @param {String} hash - md5 hash of published content (Optional)
 *  @param {Object} options
 *  @param {Boolean} options.waitForPublish Wait for the latest scene version to be published (default false);
 *
 */
export function fetchAndUse(sceneId, hash, options = {}) {
  return (store) => {
    return store.dispatch(fetch(sceneId, hash, options)).then(
      () => store.dispatch(useScene(sceneId))
    );
  };
};

export function loadingProgress({ getIn, dispatch }) {
  return getIn(['sceneIO', 'progress']);
};

/*
 * Have preloaded commands run, and are all assets loaded
 */
export function areAssetsLoaded(store) {
  return store.getIn(['sceneIO', 'phase']) >= SCENE_PHASES.PRELOADED && store.getIn(['sceneIO', 'loadingFiles']).size === 0;
}


/*
 * Are all assets loaded before preload commands are run
 */
export function areAssetsPreloaded(store) {
  return store.getIn(['sceneIO', 'phase']) >= SCENE_PHASES.RENDERING && store.getIn(['sceneIO', 'loadingFiles']).size === 0;
}

export function isSceneLoaded(store) {
  return store.getIn(['sceneIO', 'loaded']);
}

export function isSceneRendered(store) {
  return store.getIn(['sceneIO', 'phase']) >= SCENE_PHASES.RENDERED;
}

export function markPreloaded() {
  return (store) => {
    const preloadCommands = store.getIn(['sceneIO', 'preloadCommands']);
    if (!preloadCommands.size) return store.dispatch({ type: PRELOADED_SCENE });

    store.dispatch({ type: PRELOADING_SCENE });

    Promise.all(preloadCommands.map((fn) => {
      return Promise.resolve(fn());
    }).toArray()).then(() => {
      store.dispatch({ type: PRELOADED_SCENE });
    }).catch((err) => {
      console.log('Caught plugin error: ', err.message);
      console.log('Caught plugin error, stack: ', err.stack);
      store.dispatch({ type: PRELOADED_SCENE });
    });
  };
}

export function preloadCommand(fn) {
  return { type: PRELOAD_COMMAND, payload: fn };
}

export function markLoaded() {
  return { type: LOADED_SCENE };
}

export function markLoadingFile(id) {
  return { type: LOADING_FILE, payload: id };
};

export function markLoadedFile(id) {
  return { type: LOADED_FILE, payload: id };
};

function convertPlugs(plugArray) {
  const plugs = {};
  plugArray.forEach((plug) => {
    plugs[plug.type] = plug.operators.map((op) => {
      let name = op.name;
      let type = op.type || name;
      return [type, omit(['_id', 'primitive', 'name', 'type'], op), name];
    });
  });
  return plugs;
};

const convertNode = curry(function (store, sceneJSON, parent, json) {
  const id = json._id;
  const name = json.name;
  const type = json.type;

  const plugs = convertPlugs(json.plugs);

  store.dispatch(addNode({ id, name, type, plugs, parent }));

  forEach(convertNode(store, sceneJSON, id), json.nodes);
});

function convertFiles(store, files) {
  forEach((file) => {
    store.dispatch(addFile(Object.assign({ id: file._id }, omit(['_id', '__v'], file))));
  }, files);
}

export function convertFromJSON(store, json) {
  convertNode(store, json, null, json);
  convertFiles(store, json.files);

  mapObjIndexed((clip, id) => {
    store.dispatch(addClip(clip, id));
  }, json.clips);
};

export function writeScene(store) {
  const sceneGraph = getEvalSceneGraph(store);

  return store.write({
    nodes: JSON.stringify(sceneGraph.nodes),
    assets: getActiveAssets(store),
    clips: store.getIn(['animation', 'clips']),
  });
}

export function exportJSON(store) {
  return {
    scene: store.get('sceneGraph').toJS(),
    assets: getActiveAssets(store).toJS(),
  };
}

export function numErrors(store) {
  return store.getIn(['sceneIO', 'errors']).size;
}

export function getErrors(store) {
  return store.getIn(['sceneIO', 'errors']);
}

export function getScenePhase(store) {
  return store.getIn(['sceneIO', 'phase']);
}


/**
 *  Clears current scene
 *
 *  @public
 *  @returns {Promise}
 */
export function clearScene() {
  return { type: CLEAR_SCENE };
}

const publicApi = {
  reducer,
  actions: { fetch, fetchAndUse, loadScene, useScene, reportError, preloadCommand, savePublished, clearScene },
  selectors: { isSceneLoaded, isSceneRendered, loadingProgress, getErrors, numErrors, getSceneId, writeScene },
};
export default publicApi;




// WEBPACK FOOTER //
// ./src/modules/sceneIO.js